import { useAnalyticsService } from '~/composables/core';
import { useUnifiedErrorHandler } from '~/composables/unified/useUnifiedErrorHandler';
import { EVENTS } from '~/constants/events';
import { SUBMIT_LEARNING_FEEDBACK_SURVEY } from '~/graphql/documents/feedbackSurvey';

/**
 * How a student got out of the survey: the skip button under it, or the close button that
 * takes down the screen it sits on.
 */
export type FeedbackSurveyExit = 'skip_button' | 'closed';

/**
 * Where the survey is being answered: one place, named the way the server names it. A lesson is
 * a collection whose answers a teacher reads, which is why it is not a place of its own here.
 */
export type FeedbackSurveyPlace
  = | { collectionId: string }
    | { learningAssetId: string };

/**
 * The place a survey is answered in, with the context its events carry: an activity's survey, a
 * lesson's (a collection a teacher reads) or a collection's. Every survey event names the place
 * this way, in-app or external, so the started → completed/skipped funnel joins up per context.
 */
export const feedbackSurveyPlaceProps = (
  place: FeedbackSurveyPlace,
  { sharedWithTeacher = false }: { sharedWithTeacher?: boolean } = {},
) => {
  if ('learningAssetId' in place) {
    return { ...place, context: 'learning_asset' as const };
  }

  return { ...place, context: sharedWithTeacher ? 'lesson' as const : 'collection' as const };
};

export const useFeedbackSurvey = (
  place: Ref<FeedbackSurveyPlace>,
  questionCount: Ref<number>,
  /**
   * Whether a teacher reads these answers, which is what makes a collection's survey a lesson's.
   * Carried on every event as its context, so the started → completed/skipped funnel can be read
   * per context — a lesson survey, a collection survey and an activity survey are different
   * questions with different answers.
   */
  sharedWithTeacher: Ref<boolean>,
) => {
  const client = useUrqlClient();
  const analytics = useAnalyticsService();
  const { handleError } = useUnifiedErrorHandler();
  const submitting = ref(false);

  const placeProps = computed(() => feedbackSurveyPlaceProps(place.value, { sharedWithTeacher: sharedWithTeacher.value }));

  /** The in-app questionnaire's events; an externally-served survey reports `external`. */
  const inAppProps = computed(() => ({ ...placeProps.value, deliveryType: 'in_app' as const }));

  const trackStarted = () => {
    analytics.track(EVENTS.UNIFIED_CONTENT_FEEDBACK_SURVEY_STARTED, inAppProps.value);
  };

  /**
   * A completion the provider collected and the return leg redeemed. Its `started` fired when
   * the student launched it from the coach card, in their other tab. Sent immediately because the
   * return tab closes itself on the way out of the celebration, and a queued event dies with it.
   */
  const trackExternalCompleted = (coinsEarned: number) => {
    analytics.track(EVENTS.UNIFIED_CONTENT_FEEDBACK_SURVEY_COMPLETED, {
      ...placeProps.value,
      deliveryType: 'external',
      coinsEarned,
    }, { immediate: true });
  };

  /**
   * A student leaving the survey without submitting it.
   *
   * `exit` is how they left, and it is the only thing separating the two on a lesson survey: that
   * one hides the skip button, so every student who leaves one leaves by closing the screen.
   * Without this the lesson funnel would show a `started` with nothing after it and no way to tell
   * an abandon from a crash.
   *
   * Deliberately analytics only: no row is written. The responses table is unique on
   * (context, user) and the submit is first-write-wins, so a "skipped" row would permanently lock
   * that student out of ever answering. This gives Year13 the started-to-completed funnel it
   * wanted without that risk.
   */
  const trackSkipped = (answeredCount: number, exit: FeedbackSurveyExit) => {
    analytics.track(EVENTS.UNIFIED_CONTENT_FEEDBACK_SURVEY_SKIPPED, {
      ...inAppProps.value,
      questionCount: questionCount.value,
      answeredCount,
      exit,
    });
  };

  /**
   * Submits the answers via GraphQL and tracks the analytics event on success.
   * @param responses - The responses to submit.
   * @returns Whether the submission landed, and what it paid. `coinsAwarded` is what THIS call
   * deposited — 0 on a survey that pays nothing, and 0 on the retry the server absorbed.
   */
  const submit = async (responses: Record<string, unknown>): Promise<{ submitted: boolean; coinsAwarded: number }> => {
    submitting.value = true;
    try {
      const result = await client.mutation(
        SUBMIT_LEARNING_FEEDBACK_SURVEY,
        {
          input: {
            ...place.value,
            responses: JSON.stringify(responses),
          },
        },
      );

      if (result.error) {
        handleError(result.error, {
          logMessage: 'Failed to submit feedback survey',
          context: { ...place.value },
        });
        return { submitted: false, coinsAwarded: 0 };
      }

      const coinsAwarded = result.data?.submitLearningFeedbackSurvey?.coinsAwarded ?? 0;

      analytics.track(EVENTS.UNIFIED_CONTENT_FEEDBACK_SURVEY_COMPLETED, {
        ...inAppProps.value,
        questionCount: questionCount.value,
        coinsEarned: coinsAwarded,
      });

      return { submitted: true, coinsAwarded };
    }
    finally {
      submitting.value = false;
    }
  };

  return {
    submitting,
    trackStarted,
    trackSkipped,
    trackExternalCompleted,
    submit,
  };
};
